import type { TeamSegment } from "@/components/pages/analytics/front-desk/components/team/types";
import { mapOverviewToSummary } from "@/components/pages/analytics/front-desk/components/team/teamsTabMappers";
import type { TeamsOverviewResponse } from "@/types/analytics-teams";
import type { TenantTeamsTableRow } from "@/types/analytics-tenant";
import type { MetricCardTrend } from "@/components/pages/analytics/front-desk/components/shared/MetricCard";
import type {
  AdminHotelMetricRow,
  AdminMetricCell,
} from "../types/adminHotelMetricTable";

const DELTA_SUFFIX = " from previous period";

export type TeamKpiMetricKey =
  | "tasksCompleted"
  | "tasksCancelled"
  | "avgTimeOnTask"
  | "delays"
  | "daysOff";

export type TeamKpiComparisonByKey = Partial<
  Record<TeamKpiMetricKey, { delta?: string; trend?: MetricCardTrend }>
>;

function percentChange(current: number, previous: number): number | null {
  if (current === previous) {
    return 0;
  }

  if (previous === 0) {
    return current === 0 ? 0 : 100;
  }

  return Number((((current - previous) / previous) * 100).toFixed(1));
}

function formatPercentDelta(changePct: number | null): string | undefined {
  if (changePct == null) {
    return undefined;
  }

  const sign = changePct > 0 ? "+" : "";
  const formatted = changePct.toLocaleString("en-US", {
    maximumFractionDigits: 1,
  });

  return `${sign}${formatted}%${DELTA_SUFFIX}`;
}

function trendFromChange(
  changePct: number | null,
  higherIsBetter: boolean,
): MetricCardTrend {
  if (changePct == null || changePct === 0) {
    return "neutral";
  }

  if (higherIsBetter) {
    return changePct > 0 ? "positive" : "negative";
  }

  return changePct < 0 ? "positive" : "negative";
}

function buildComparisonDelta(
  current: number,
  previous: number,
  higherIsBetter: boolean,
): { delta?: string; trend?: MetricCardTrend } {
  const changePct = percentChange(current, previous);

  return {
    delta: formatPercentDelta(changePct),
    trend: trendFromChange(changePct, higherIsBetter),
  };
}

export function buildAdminTeamKpiComparison(
  current: TeamsOverviewResponse | null | undefined,
  previous: TeamsOverviewResponse | null | undefined,
  segment: TeamSegment,
): TeamKpiComparisonByKey {
  if (!current || !previous) {
    return {};
  }

  const comparison: TeamKpiComparisonByKey = {
    tasksCompleted: buildComparisonDelta(
      current.tasksCompleted,
      previous.tasksCompleted,
      true,
    ),
    tasksCancelled: buildComparisonDelta(
      current.tasksCanceled,
      previous.tasksCanceled,
      false,
    ),
    avgTimeOnTask: buildComparisonDelta(
      current.avgTimeOnTask.avgSeconds,
      previous.avgTimeOnTask.avgSeconds,
      false,
    ),
  };

  if (segment === "staff") {
    comparison.delays = buildComparisonDelta(
      current.delays,
      previous.delays,
      false,
    );
    comparison.daysOff = buildComparisonDelta(
      current.dayOff,
      previous.dayOff,
      false,
    );
  }

  return comparison;
}

function toTableDelta(changePct: number | null, higherIsBetter: boolean) {
  const trend = trendFromChange(changePct, higherIsBetter);

  return {
    delta: formatPercentDelta(changePct),
    trend,
  };
}

function buildMetricCell(
  value: string,
  changePct: number | null,
  higherIsBetter: boolean,
): AdminMetricCell {
  return {
    value,
    ...toTableDelta(changePct, higherIsBetter),
  };
}

function formatCount(value: number): string {
  return value.toLocaleString("en-US");
}

export function mapTeamHotelRows(
  hotels: TenantTeamsTableRow[],
  currentByHotelId: Map<string, TeamsOverviewResponse | null>,
  previousByHotelId: Map<string, TeamsOverviewResponse | null>,
  hotelLogosById: Record<string, string | null>,
  segment: TeamSegment,
): AdminHotelMetricRow[] {
  return hotels.map((hotel) => {
    const current = mapOverviewToSummary(
      currentByHotelId.get(hotel.hotelId) ?? undefined,
    );
    const previous = mapOverviewToSummary(
      previousByHotelId.get(hotel.hotelId) ?? undefined,
    );

    const tasksCompletedChange = percentChange(
      current.tasksCompleted,
      previous.tasksCompleted,
    );
    const tasksCancelledChange = percentChange(
      current.tasksCancelled,
      previous.tasksCancelled,
    );
    const delaysChange = percentChange(current.delays, previous.delays);
    const daysOffChange = percentChange(current.daysOff, previous.daysOff);
    const currentOverview = currentByHotelId.get(hotel.hotelId);
    const previousOverview = previousByHotelId.get(hotel.hotelId);
    const avgTimeChange = percentChange(
      currentOverview?.avgTimeOnTask.avgSeconds ?? 0,
      previousOverview?.avgTimeOnTask.avgSeconds ?? 0,
    );

    const metrics: AdminHotelMetricRow["metrics"] = {
      tasksCompleted: buildMetricCell(
        formatCount(current.tasksCompleted),
        tasksCompletedChange,
        true,
      ),
      tasksCancelled: buildMetricCell(
        formatCount(current.tasksCancelled),
        tasksCancelledChange,
        false,
      ),
      avgTimeOnTask: buildMetricCell(
        current.avgTimeOnTask,
        avgTimeChange,
        false,
      ),
    };

    if (segment === "staff") {
      metrics.delays = buildMetricCell(
        formatCount(current.delays),
        delaysChange,
        false,
      );
      metrics.daysOff = buildMetricCell(
        formatCount(current.daysOff),
        daysOffChange,
        false,
      );
    }

    return {
      id: hotel.hotelId,
      hotelBrand: hotel.hotelName,
      hotelLogoId: hotelLogosById[hotel.hotelId] ?? null,
      metrics,
    };
  });
}
